/**
 * Notes Service
 * 
 * Handles analyst notes CRUD operations.
 * Connects to func-axn-analyst-api via SWA linked backend.
 * 
 * Three visibility tiers:
 * 1. Internal (isInternal=true) - Only visible in swa-axn-analyst
 * 2. Customer-visible (isInternal=false) - Synced to axncustomerdb, visible in swa-axn-customer
 * 3. Defender-synced (syncedToDefender=true) - Also pushed to customer's Defender as comment
 * 
 * API endpoints:
 * - GET  /api/incidents/:tenantId/:incidentId/notes
 * - POST /api/incidents/:tenantId/:incidentId/notes
 */

import type { AnalystNote, NewNote, NoteType } from '../data/types';
import { apiFetch, apiPost, apiDelete, apiPatch, type ApiResponse } from './api';
import { addDefenderIncidentComment } from './defender';

// ============================================================================
// API Response Types
// ============================================================================

interface NotesResponse {
  notes: Array<{
    noteId: string;
    authorUserId: string;
    authorName: string;
    noteType: string;
    content: string;
    isInternal: boolean;
    isPinned: boolean;
    createdAt: string;
    updatedAt: string;
  }>;
}

interface CreateNoteResponse {
  note: {
    noteId: string;
    authorUserId: string;
    authorName: string;
    noteType: string;
    content: string;
    isInternal: boolean;
    isPinned: boolean;
    createdAt: string;
    updatedAt: string;
  };
}

// ============================================================================
// TRANSFORM HELPERS
// ============================================================================

function transformApiNote(
  api: NotesResponse['notes'][0],
  tenantId: string,
  incidentId: string
): AnalystNote {
  return {
    noteId: api.noteId,
    tenantId,
    incidentId,
    authorUserId: api.authorUserId,
    authorName: api.authorName,
    noteType: api.noteType as NoteType,
    content: api.content,
    isInternal: api.isInternal,
    isPinned: api.isPinned,
    syncedToDefender: false, // TODO: Track this in DB
    createdAt: api.createdAt,
    updatedAt: api.updatedAt,
  };
}

// ============================================================================
// GET NOTES
// ============================================================================

/**
 * Fetches all notes for an incident
 */
export async function getNotes(
  tenantId: string,
  incidentId: string
): Promise<ApiResponse<AnalystNote[]>> {
  const result = await apiFetch<NotesResponse>(
    `/incidents/${encodeURIComponent(tenantId)}/${encodeURIComponent(incidentId)}/notes`
  );
  
  if (!result.success) {
    return { data: [], success: false, error: result.error };
  }

  const notes = result.data.notes.map(n => transformApiNote(n, tenantId, incidentId));
  return { data: notes, success: true };
}

/**
 * Gets notes filtered by visibility
 */
export async function getNotesByVisibility(
  tenantId: string,
  incidentId: string,
  isInternal?: boolean
): Promise<ApiResponse<AnalystNote[]>> {
  const result = await getNotes(tenantId, incidentId);
  
  if (!result.success) {
    return result;
  }

  let notes = result.data;
  if (isInternal !== undefined) {
    notes = notes.filter(n => n.isInternal === isInternal);
  }
  
  return { data: notes, success: true };
}

// ============================================================================
// CREATE NOTE
// ============================================================================

interface CreateNoteApiResponse extends CreateNoteResponse {
  syncedToDefender?: boolean;
  defenderCommentId?: string;
}

/**
 * Adds a new note to an incident
 * If syncToDefender is true and isInternal is false, the note will be synced to Defender's Activity Log
 */
export async function addNote(
  tenantId: string,
  incidentId: string,
  note: NewNote
): Promise<ApiResponse<AnalystNote>> {
  const result = await apiPost<CreateNoteApiResponse>(
    `/incidents/${encodeURIComponent(tenantId)}/${encodeURIComponent(incidentId)}/notes`,
    {
      noteType: note.noteType,
      content: note.content,
      isInternal: note.isInternal,
      syncToDefender: note.syncToDefender,
    }
  );
  
  if (!result.success) {
    return { 
      data: null as unknown as AnalystNote, 
      success: false, 
      error: result.error 
    };
  }

  const transformedNote = transformApiNote(result.data.note, tenantId, incidentId);
  
  // Update sync status from response
  if (result.data.syncedToDefender) {
    transformedNote.syncedToDefender = true;
    transformedNote.defenderCommentId = result.data.defenderCommentId;
  }

  return { 
    data: transformedNote, 
    success: true 
  };
}

// ============================================================================
// DELETE NOTE
// ============================================================================

/**
 * Deletes a note by ID
 * Uses the incidents API path since notes are scoped to incidents
 */
export async function deleteNote(
  tenantId: string,
  incidentId: string,
  noteId: string
): Promise<ApiResponse<void>> {
  const result = await apiDelete<void>(
    `/incidents/${encodeURIComponent(tenantId)}/${encodeURIComponent(incidentId)}/notes/${encodeURIComponent(noteId)}`
  );
  
  if (!result.success) {
    return { data: undefined, success: false, error: result.error };
  }
  
  return { data: undefined, success: true };
}

// ============================================================================
// UPDATE NOTE
// ============================================================================

/**
 * Updates a note's content
 */
export async function updateNoteContent(
  tenantId: string,
  incidentId: string,
  noteId: string,
  content: string
): Promise<ApiResponse<void>> {
  const result = await apiPatch<{ note: NotesResponse['notes'][0] }>(
    `/incidents/${encodeURIComponent(tenantId)}/${encodeURIComponent(incidentId)}/notes/${encodeURIComponent(noteId)}`,
    { content }
  );
  
  if (!result.success) {
    return { data: undefined, success: false, error: result.error };
  }
  
  return { data: undefined, success: true };
}

/**
 * Toggles a note's pinned status
 */
export async function toggleNotePinned(
  tenantId: string,
  incidentId: string,
  noteId: string,
  isPinned: boolean
): Promise<ApiResponse<boolean>> {
  const result = await apiPatch<{ note: NotesResponse['notes'][0] }>(
    `/incidents/${encodeURIComponent(tenantId)}/${encodeURIComponent(incidentId)}/notes/${encodeURIComponent(noteId)}`,
    { isPinned }
  );
  
  if (!result.success) {
    return { data: false, success: false, error: result.error };
  }
  
  return { data: isPinned, success: true };
}

/**
 * Changes a note's visibility (internal -> customer-visible)
 * Note: Cannot make a customer-visible note internal after sharing
 */
export async function makeNoteCustomerVisible(
  tenantId: string,
  incidentId: string,
  noteId: string
): Promise<ApiResponse<void>> {
  const result = await apiPatch<{ note: NotesResponse['notes'][0]; syncedToCustomerDb: boolean }>(
    `/incidents/${encodeURIComponent(tenantId)}/${encodeURIComponent(incidentId)}/notes/${encodeURIComponent(noteId)}`,
    { isInternal: false }
  );
  
  if (!result.success) {
    return { data: undefined, success: false, error: result.error };
  }
  
  return { data: undefined, success: true };
}

// ============================================================================
// DEFENDER SYNC
// ============================================================================

/**
 * Syncs a note to the customer's Defender environment as a comment
 * This uses the Defender write-back API
 * 
 * Note must already be customer-visible (isInternal=false)
 * This is a one-way push; the comment cannot be un-synced
 */
export async function syncNoteToDefender(
  tenantId: string,
  incidentId: string,
  noteContent: string
): Promise<ApiResponse<string>> {
  // Use the Defender service to add the comment
  const result = await addDefenderIncidentComment(tenantId, incidentId, noteContent);
  
  if (!result.success) {
    return { data: '', success: false, error: result.error };
  }

  return { data: result.data.defenderId || '', success: true };
}

/**
 * Gets the sync status for a note
 * Note: This would need DB tracking of synced notes
 */
export async function getNoteSyncStatus(
  _noteId: string
): Promise<ApiResponse<{ synced: boolean; defenderCommentId?: string }>> {
  // TODO: Track sync status in database
  return { data: { synced: false }, success: true };
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Gets note type display label
 */
export function getNoteTypeLabel(type: NoteType): string {
  const labels: Record<NoteType, string> = {
    comment: 'Comment',
    investigation_note: 'Investigation Note',
    recommendation: 'Recommendation',
    escalation: 'Escalation',
  };
  return labels[type];
}

/**
 * Gets note type color class
 */
export function getNoteTypeColor(type: NoteType): string {
  const colors: Record<NoteType, string> = {
    comment: 'bg-slate-500',
    investigation_note: 'bg-blue-500',
    recommendation: 'bg-amber-500',
    escalation: 'bg-red-500',
  };
  return colors[type];
}
